<template>
  <ScrollTop v-if="showScrollTop" />
  <div class="englobe-params">
    <div class="params-row top">
      <FiltersMenu
        :active-filters="activeFilters"
        :is-loading="!store.filters"
        @remove-filter="removeFilterItem"
        @reset-all-filters="resetAllFilters"
      >
        <SearchAsYouTypeFilter
          v-if="!store.isFollawedActive"
          filter="author"
          :items="store.authors"
          item-label="name"
          item-value="_id"
          :active-filters="selectedFilters.author"
          :is-loading="store.isAuthorFilterLoading"
          @search="fetchSearchAuthors"
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetAuthorFilter"
        >
          Auteurs
        </SearchAsYouTypeFilter>
        <SelectMultipleCounter
          v-for="(value, key) in filtersNames"
          :key="key"
          :filter="key"
          :items="store.filters[key]"
          :active-filters="selectedFilters[key]"
          has-search
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetFilter"
        >
          {{ value }}
        </SelectMultipleCounter>
      </FiltersMenu>
      <FollawedRadioButtons
        all-match-route="/posts"
        follawed-route="/posts/follawed"
      />
    </div>

    <div class="params-row request-and-check-all">
      <SwitchSlide :is-active="!store.bypassQuery" @toggle="toggleUseRequest">
        Utiliser la requête
      </SwitchSlide>
      <CheckBox
        :checked="isAllSelected"
        @update:checked="
          () => (isAllSelected ? unselectAllFilters() : selectAllFilters())
        "
      >
        Toutes les activités
      </CheckBox>
    </div>
    <div class="params-row">
      <div class="page-settings filters">
        <FilterCheckBox
          v-for="itemSocialMedia in itemsFiltersSocialMedia"
          :key="itemSocialMedia.label"
          :label="itemSocialMedia.label"
          :color="itemSocialMedia.color"
          :item-count="
            store.postsCounts[`${itemSocialMedia.indexes}_count`] || 0
          "
          :is-checked="indexesFilters.includes(itemSocialMedia.indexes)"
          @check="addFilter(itemSocialMedia.indexes)"
          @uncheck="removeFilter(itemSocialMedia.indexes)"
        />
      </div>
    </div>

    <div class="params-row">
      <TotalMatched :item-count="store.item_count" />

      <div class="page-settings">
        <DropdownMenu
          v-model="selectedSorterOption"
          :options="sortersOptions"
          label="Trier par :"
        />

        <DropdownMenu
          v-model="selectedLimitOption"
          :options="limitOptions"
          label="Publications par page :"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useSocialMediaStore } from "@follaw-fr/stores/social-media/posts";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import FiltersMenu from "@follaw-fr/components/filter-menu/organisms/FiltersMenu.vue";
import FollawedRadioButtons from "@/follaw-fr/components/reusable/atoms/FollawedRouterRadioButtons.vue";
import SelectMultipleCounter from "@follaw-fr/components/filter-menu/molecules/SelectMultipleCounter.vue";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";
import TotalMatched from "@follaw-fr/components/reusable/atoms/TotalMatched.vue";
import SearchAsYouTypeFilter from "@follaw-fr/components/filter-menu/molecules/SearchAsYouTypeFilter.vue";
import ScrollTop from "@follaw-fr/components/reusable/atoms/ScrollTop.vue";
import FilterCheckBox from "@follaw-fr/components/reusable/atoms/FilterCheckBox.vue";
import SwitchSlide from "@follaw-fr/components/reusable/atoms/SwitchSlide.vue";
import CheckBox from "@follaw-fr/components/reusable/atoms/CheckBox.vue";
import { useDropdown } from "@follaw-fr/composables/useDropdown";
import {
  Platforms,
  PLATFORM_TYPES,
} from "@follaw-fr/utils/social360-platforms.js";

const store = useSocialMediaStore();
const route = useRoute();

const { selectedFilters, indexesFilters } = storeToRefs(store);

const toggleUseRequest = () => {
  store.bypassQuery = !store.bypassQuery;
  store.getPublicationsList();
};

const fetchPublications = async () => {
  selectedFilters.value.from = 0;
  await store.getPublicationsList(route.query.author_id ?? null);
};

const fetchSearchAuthors = (nameSearch) => {
  if (nameSearch.length > 0) {
    store.getSearchAuthorsFilters(nameSearch);
  } else {
    store.resetSearchAuthorsFilters();
  }
};

const { options: sortersOptions, selectedValue: selectedSorterOption } =
  useDropdown({
    options: { created_at: "Date", like: "Engagement" },
    selectedFilters,
    filterKey: "sort_field", // Matches a key in initialPublicationsFilters
    onChange: fetchPublications,
  });

const { options: limitOptions, selectedValue: selectedLimitOption } =
  useDropdown({
    options: { 10: "10", 20: "20", 50: "50", 200: "200" },
    selectedFilters,
    filterKey: "size", // Matches a key in initialPublicationsFilters
    onChange: fetchPublications,
  });

const showScrollTop = computed(() => selectedFilters.value.size > 20);

const isAllSelected = computed(() => {
  return indexesFilters.value.length === Object.keys(Platforms)?.length;
});

const itemsFiltersSocialMedia = ref([
  {
    label: "𝕏",
    color: "var(--clr-twitter)",
    indexes: Platforms.TWITTER,
  },
  {
    label: "Linkedin",
    color: "var(--clr-linkedin)",
    indexes: Platforms.LINKEDIN,
  },
  {
    label: "Instagram",
    color: "var(--clr-instagram)",
    indexes: Platforms.INSTAGRAM,
  },
  {
    label: "Tiktok",
    color: "var(--clr-tiktok)",
    indexes: Platforms.TIKTOK,
  },
  {
    label: "Bluesky",
    color: "var(--clr-bluesky)",
    indexes: Platforms.BLUESKY,
  },
]);

// All filters in wanted display order with their labels
const filtersNames = {
  group: "Groupe",
  major_stakeholder: "Grande Partie Prenante",
  stakeholder: "Partie Prenante",
  level: "Niveau",
  circo_name: "Localité",
};

const activeFilters = computed(() => {
  let arr = [];
  // Put all selected options from all filters with said filter name in an array for the SelectedFilters component
  Object.keys(filtersNames).forEach((key) => {
    selectedFilters.value[key]?.forEach((filter) => {
      arr = [...arr, { filter: key, item: filter }];
    });
  });
  selectedFilters.value.author?.forEach((filter) => {
    arr = [
      ...arr,
      {
        filter: "author",
        item: filter,
      },
    ];
  });
  return arr;
});

const selectAllFilters = () => {
  indexesFilters.value = [...PLATFORM_TYPES];
  store.getPublicationsList();
};

const unselectAllFilters = () => {
  indexesFilters.value = [];
  store.getPublicationsList();
};

const addFilter = (filter) => {
  indexesFilters.value.push(filter);
  store.getPublicationsList();
};

const addFilterItem = (event) => {
  const targetFilters = selectedFilters.value[event.filter] || [];

  selectedFilters.value = {
    ...selectedFilters.value,
    [event.filter]: [...targetFilters, event.item],
  };
  fetchPublications();
};

const removeFilter = (filter) => {
  indexesFilters.value = indexesFilters.value.filter((f) => f !== filter);
  store.getPublicationsList();
};

const removeFilterItem = (event) => {
  let targetFilters = selectedFilters.value[event.filter] ?? [];

  targetFilters = targetFilters.filter((el) => el !== event.item);

  if (targetFilters.length === 0) {
    delete selectedFilters.value[event.filter];
  } else {
    selectedFilters.value = {
      ...selectedFilters.value,
      [event.filter]: targetFilters,
    };
  }

  fetchPublications();
};

const resetFilter = (event) => {
  delete selectedFilters.value[event];
  fetchPublications();
};

const resetAuthorFilter = () => {
  store.resetSearchAuthorsFilters();
  delete selectedFilters.value.author;
  fetchPublications();
};

const resetAllFilters = () => {
  store.resetFilters();
  fetchPublications();
};
</script>

<style lang="scss" scoped>
.request-and-check-all {
  justify-content: flex-start;
  gap: 15px;
}

.filters {
  gap: 10px;
}

.filter {
  width: auto;
  gap: 40px;
}
</style>
